import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { isVideoPath } from "@/lib/api";
import { SmartImage } from "./SmartMedia";
import { BeforeAfter } from "./BeforeAfterSlider";
import { ShareButton } from "./ShareButton";
import { useViewerGestures } from "./useViewerGestures";

const isVideo = (it) => it.kind === "video" || (it.kind === "path" && isVideoPath(it.path));

// One slide. Only the visible slide carries the item's testid and plays video.
// items: { kind: "image", src, alt } | { kind: "video", src, poster } | { kind: "pair", beforeSrc, afterSrc, share? }
//        | { kind: "path", path, resolve }  — each may add caption and testid. share() → a public link.
const Slide = ({ item, active, slot }) => {
  const tid = active ? item.testid : undefined;
  if (item.kind === "pair") {
    return <BeforeAfter beforeSrc={item.beforeSrc} afterSrc={item.afterSrc} caption={item.caption}
      testid={tid || `fullscreen-pair-${slot}`} fill dragAnywhere />;
  }
  if (isVideo(item) && !active) return <div className="w-full h-full bg-black" />;
  if (item.kind === "path") {
    return <SmartImage path={item.path} resolve={item.resolve} contain testid={tid} className="w-full h-full" />;
  }
  if (item.kind === "video") {
    return <video src={item.src} poster={item.poster} data-testid={tid} className="w-full h-full object-contain"
      controls autoPlay muted loop playsInline />;
  }
  return <img src={item.src} alt={item.alt || item.caption || "Photo"} data-testid={tid} draggable={false}
    className="w-full h-full object-contain" />;
};

const BTN = "pointer-events-auto flex items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md hover:bg-black/75 active:bg-black/80 transition-colors";

// True fullscreen, edge-to-edge photo viewer (rendered on <body>, above every dialog) that
// behaves like the iPhone camera roll: pinch or double-tap to zoom, drag to pan, swipe left/right
// between photos, swipe down or the top-left Close to close, tap to hide/show the controls.
// A before & after pair is different: every drag moves its slider; the arrows page and Close closes.
export const FullscreenViewer = ({ items, index, onNav, onClose, ids = {}, action }) => {
  const [chrome, setChrome] = useState(true);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const count = items.length;
  const item = items[index];
  const isPair = item?.kind === "pair";
  const zoomable = !!item && !isVideo(item) && !isPair; // a pair's drag is its slider
  const { rootRef, trackRef, mediaRef, page, handlers } = useViewerGestures({
    index, count, zoomable, onNav, onClose, onToggleChrome: () => setChrome((c) => !c),
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); closeRef.current(); }
      else if (e.key === "ArrowRight") { e.stopPropagation(); page(1); }
      else if (e.key === "ArrowLeft") { e.stopPropagation(); page(-1); }
    };
    document.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [page]);

  if (!item) return null;
  const many = count > 1;
  const fade = `transition-opacity duration-200 ${chrome || isPair ? "opacity-100" : "opacity-0 pointer-events-none"}`; // a pair can't tap-toggle — keep its arrows
  const hint = (isPair
    ? ["Drag left or right to compare", many && "Use the arrows for the next one"]
    : [zoomable && "Pinch to zoom", many && "Swipe for more"]).filter(Boolean).join(" · ");
  const slots = [index - 1, index, index + 1].filter((k) => k >= 0 && k < count);

  return createPortal(
    <div
      ref={rootRef}
      data-fullscreen-viewer=""
      data-testid={ids.root}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-[200] bg-black text-white select-none touch-none overscroll-none pointer-events-auto"
      style={{ WebkitTouchCallout: "none" }}
    >
      <div ref={trackRef} className="absolute inset-0 will-change-transform" data-testid={ids.root ? `${ids.root}-stage` : undefined} {...handlers}>
        {slots.map((k) => (
          <div key={k} className="absolute inset-0 overflow-hidden" style={{ transform: `translate3d(${(k - index) * 100}%, 0, 0)` }}>
            <div ref={k === index ? mediaRef : undefined} className="w-full h-full flex items-center justify-center will-change-transform"
              style={{ transform: k === index ? undefined : "none" }}>
              <Slide item={items[k]} active={k === index} slot={k < index ? "prev" : k > index ? "next" : "current"} />
            </div>
          </div>
        ))}
      </div>

      <div className="absolute inset-x-0 top-0 z-20 flex items-center gap-3 px-3 pb-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none"
        style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}>
        <button type="button" onClick={onClose} aria-label="Close" data-testid={ids.close}
          className={`${BTN} h-12 shrink-0 gap-1.5 pl-3 pr-4 ring-1 ring-white/30 !bg-black/75`}>
          <X className="h-6 w-6" /> <span className="text-sm font-semibold">Close</span>
        </button>
        {many && (
          <p data-testid={ids.counter}
            className={`absolute left-1/2 -translate-x-1/2 font-mono text-sm text-white/90 tabular-nums whitespace-nowrap ${fade}`}>
            {index + 1} / {count}
          </p>
        )}
        <div className={`ml-auto flex items-center justify-end gap-2 ${fade}`}>
          {isPair && item.share && (
            <ShareButton item={item} testid={ids.root ? `${ids.root}-share` : "fullscreen-share"}
              className={`${BTN} h-12 shrink-0 gap-1.5 pl-3 pr-4 ring-1 ring-white/30 !bg-black/75`} />
          )}
          {action?.(index)}
        </div>
      </div>

      {many && index > 0 && (
        <button type="button" onClick={() => page(-1)} aria-label="Previous photo" data-testid={ids.prev}
          className={`${BTN} absolute left-3 top-1/2 -mt-7 h-14 w-14 ${fade}`}>
          <ChevronLeft className="h-7 w-7" />
        </button>
      )}
      {many && index < count - 1 && (
        <button type="button" onClick={() => page(1)} aria-label="Next photo" data-testid={ids.next}
          className={`${BTN} absolute right-3 top-1/2 -mt-7 h-14 w-14 ${fade}`}>
          <ChevronRight className="h-7 w-7" />
        </button>
      )}

      {(item.caption || hint) && (
        <div className={`absolute inset-x-0 bottom-0 px-5 pt-10 text-center bg-gradient-to-t from-black/70 to-transparent pointer-events-none ${fade}`}
          style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
          {item.caption && <p data-testid={ids.caption} className="text-sm text-white/90">{item.caption}</p>}
          {hint && <p className="text-[11px] text-white/60 mt-1">{hint}</p>}
        </div>
      )}
    </div>,
    document.body
  );
};
